import { CountryCode } from 'libphonenumber-js';
import { LeadWebhookFormat } from './BusinessConfig';
import { AttrWindowsBySourceType } from '../insights';
import { User } from '../user';
import { ExtrapolationType, StageTarget, Support } from '../funnel';

export type LegacyBusinessUpdateable = Partial<
  Pick<
    LegacyBusiness,
    'first_initial_purchase' | 'yvc_party_size' | 'yvc_spend' | 'yvc_visits'
  >
>;

/**
 * Shared externally (with other apps not in our code base).
 *
 * Should not change.
 */
export interface BusinessPublic {
  id: number;
  businessName: string;
}

export const exampleBusinessPublic: BusinessPublic = {
  id: 1,
  businessName: 'Example Business',
};

export enum BusinessPhase {
  CRAWL = 1,
  WALK = 2,
  RUN = 3,
  FLY = 4,
}

export interface Business extends BusinessPublic {
  branchID: number;
  branchName: string;
  podID: number;
  podName: string;
  isLegacy: boolean;
  currencyType: string;
  locale: string;
  timezone: string;
  fbPageImgURL?: string;
  fbAccountID: string;
  fbAdAuthorizerID?: number | null;
  fbPageID?: string;
  googleType?: string;
  latitude?: number;
  longitude?: number;
  fbOfflineEventSetID?: string;
  fbDatasetID?: string;
  startDate: string | null | undefined;
  errors?: BusinessError[];
  semRushID?: number | null;
  googleLookerEmbedURL?: string | null;
  reportsPageActive?: boolean;
  dataUploaderActive?: boolean;
  pixelIndicator?: string;
  dataIndicator?: string;
  uploadIndicator?: string;
  pixelIndicatorDesc?: string;
  dataIndicatorDesc?: string;
  uploadIndicatorDesc?: string;
  allowLeadCollection?: boolean;
  country?: CountryCode;
  googleAdWordsClientCustomerID?: string | null;
  closePercentage?: number | null;
  showROI?: boolean;
  monthlySpend?: number | null;
  mktgRetainer?: number | null;
  yearlyPartySize?: number | null;
  yearlyVisits?: number | null;
  yearlyValueSpend?: number | null;
  website?: string;
  googleDriveFolderID?: string | null;
  googleAdsCustomerID?: string | null;
  phase?: BusinessPhase;
  slackChannelId?: string | null;
  // The business's Salesforce Account id. Empty means nobody has connected this
  // business to Salesforce yet — the Sprint Packet survey card reads it to name
  // that as the reason it has no survey link.
  sfAccountId?: string | null;
}

export const exampleBusiness: Business = {
  branchID: 1,
  branchName: 'Test',
  podID: 1,
  podName: 'Test',
  isLegacy: false,
  currencyType: 'USD',
  locale: 'en-US',
  timezone: 'America/Chicago',
  fbPageImgURL: 'fb.com/imglink',
  fbAccountID: '123',
  fbPageID: '321',
  fbOfflineEventSetID: '1234',
  startDate: new Date().toString(),
  semRushID: 123,
  googleLookerEmbedURL: 'google.com/embed',
  reportsPageActive: true,
  dataUploaderActive: true,
  pixelIndicator: 'Facebook Pixel Activity',
  dataIndicator: 'Data Activity',
  uploadIndicator: 'Upload Activity',
  pixelIndicatorDesc:
    'This shows that your Facebook Pixel is active on your website',
  dataIndicatorDesc:
    'This indicator shows that your data source is linked into our system',
  uploadIndicatorDesc:
    'This indicator shows that your data has been processed by Marketing Milk',
  allowLeadCollection: true,
  country: 'US',
  googleAdWordsClientCustomerID: '123',
  closePercentage: 1,
  showROI: true,
  monthlySpend: 1000,
  mktgRetainer: 1000,
  yearlyPartySize: 1000,
  yearlyVisits: 1000,
  yearlyValueSpend: 1000,
  website: 'example.com',
  phase: BusinessPhase.CRAWL,
  ...exampleBusinessPublic,
};

export enum BusinessError {
  ad_account_access_error = 'ad_account_access_error',
  page_access_error = 'page_access_error',
  lead_access_error = 'lead_access_error',
  offline_upload_error = 'offline_upload_error',
  google_ads_access_error = 'google_ads_access_error',
}

export type AuthorizerConnectionType = 'fb_ad' | 'fb_page';

export interface AuthorizerConnection {
  business_id: number;
  business_name: string;
  connection_type: AuthorizerConnectionType;
  errors?: BusinessError[];
}

export interface AuthorizerConnectionsByUser {
  user_id: number;
  user_name: string;
  user_email: string;
  user_profile_pic?: string | null;
  connections: AuthorizerConnection[];
}

export interface AuthorizationsPage {
  data: AuthorizerConnectionsByUser[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuthorizerTokenStatus {
  user_id: number;
  is_valid: boolean;
  expires_at?: number | null;
  scopes?: string[];
}

export type SystemUserAssetType =
  | 'adaccount'
  | 'page'
  | 'dataset'
  | 'offline'
  | 'instagram';

export interface SystemUserAssetConnection {
  business_id: number;
  business_name: string;
  asset_type: SystemUserAssetType;
  asset_id?: string;
  errors?: BusinessError[];
  monitored: boolean;
  fb_business_id: string;
}

export interface SystemUserBusinessConnection {
  system_user_id: string;
  client_business_manager_id: string;
  fb_business_name?: string;
  fb_business_picture_uri?: string;
  connections: SystemUserAssetConnection[];
}

export interface FBBusinessManagerInfo {
  id: string;
  name?: string;
  pictureUri?: string;
}

export interface BusinessManagerBusiness {
  errors?: BusinessError[];
  id?: number;
  main: BusinessManagerBusinessMain;
  funnel: {
    active: number | null;
    preset: number;
    stages: BusinessManagerStage[];
  };
  users: BusinessManagerBusinessUser[];
  options: BusinessManagerOption[];
  tags: number[];
  dataSources: number[];
  // Business Manager name/picture for each currently-assigned Facebook Builder asset's owning BM,
  // resolved from the asset's system user (see BusinessManagerController.getBusiness) — not persisted
  // on the business record itself, always resolved live off the relevant system user's clientBusinessManagerId.
  fb_business_info?: Partial<
    Record<SystemUserAssetType, FBBusinessManagerInfo>
  >;
}

export interface BusinessManagerOption {
  branch_id: number;
  business_id: number;
  branch_options_id: number;
  field_value: string;
}

export interface BusinessManagerBusinessUser {
  user_id: number;
  receive_leads: boolean;
  receive_status: boolean;
}

export interface BusinessManagerBusinessMain {
  business_name: string;
  website: string;
  fb_account_id: string;
  fb_account_name: string | null;
  fb_page_id: string | null;
  fb_page_name: string | null;
  mktg_retainer: number;
  yearly_value: number;
  start: string;
  branch_id: number | null;
  pod_id: number | null;
  fb_dataset_id: string | null;
  fb_dataset_name: string | null;
  instagram_id: string | null;
  instagram_name: string | null;
  // resolved live off the ad account's token on each business load — not a stored column, since
  // Instagram's own picture can't be derived from a guessable public URL the way Page's can.
  instagram_picture_uri?: string;
  // resolved the same way — whether this Instagram account is still listed under the current Ad
  // Account's system-user token. Absent (not false) when it couldn't be checked at all (no token, no
  // linked account) — only an explicit false means "checked and it's no longer connected."
  instagram_connected?: boolean;
  offline_event_set_id: string | null;
  fb_offline_name: string | null;
  monthly_spend: number;
  ga_view_id: number | null;
  client_customer_id: number | null;
  goal_text: string;
  yvc_party_size: number;
  yvc_visits: number;
  yvc_spend: number;
  ingest_access_token: string;
  close_percentage: number;
  timezone: string;
  attr_windows: AttrWindowsBySourceType;
  show_roi: boolean;
  pixel_indicator: string;
  pixel_indicator_desc: string;
  data_indicator: string;
  data_indicator_desc: string;
  upload_indicator: string;
  upload_indicator_desc: string;
  first_initial_purchase: number;
  phase: number;
  city: string;
  state: string;
  lead_webhook_format: LeadWebhookFormat;
  currency_type: string;
  locale: string;
  lookback_window: number;
  override_yvc: boolean;
  data_uploader_active: boolean;
  reports_page_active: boolean;
  received_product_demo: boolean;
  repeat_check_fullname_fallback: boolean;
  allow_lead_collection: boolean;
  use_offline_id: boolean;
  fb_ad_authorizer?: User;
  fb_ad_authorizer_id?: number | null;
  fb_page_authorizer?: User;
  fb_page_authorizer_id?: number | null;
  google_authorizer?: User;
  google_authorizer_id?: number | null;
  fb_leads_polling_form_ids?: string[] | null;
  address?: string | null;
  google_type?: string | null;
  google_looker_embed_url?: string | null;
  google_ads_customer_id?: string | null;
  google_ads_manager_id?: string | null;
  fb_builder: boolean;
  adaccount_system_user_id: string | null;
  page_system_user_id: string | null;
  dataset_system_user_id: string | null;
  offline_system_user_id: string | null;
  instagram_system_user_id: string | null;
  sf_account_id?: string | null;
  slack_channel_id?: string | null;
  google_drive_folder_id?: string | null;
  googleDriveFolderID?: string | null;
}

type RequireNonNullable<T, K extends keyof T> = Omit<T, K> &
  {
    [P in K]-?: NonNullable<T[P]>;
  };

type BusinessTokens = {
  fb_ad_access_token?: string | null;
  fb_page_authorizer_access_token?: string | null;
  ad_account_token?: string | null;
  page_token?: string | null;
  dataset_token?: string | null;
  offline_token?: string | null;
  instagram_token?: string | null;
};

type Authorizers = RequireNonNullable<
  BusinessTokens,
  'fb_ad_access_token' | 'fb_page_authorizer_access_token'
> & {
  ad_account_token: never;
  page_token: never;
  dataset_token: never;
  offline_token: never;
  instagram_token: never;
};

// dataset_token deliberately not required here (DRI-214): a Conversions API Dataset connected to an ad
// account no longer gets its own system-user grant at all — access is scoped through the ad account
// relationship instead, so a fully-connected system-user business now never has a dataset_token, and
// requiring it here would mean isSystemTokens() never matches a real one again.
type SystemUsers = RequireNonNullable<
  BusinessTokens,
  'ad_account_token' | 'page_token'
> & {
  fb_ad_access_token: never;
  fb_page_authorizer_access_token: never;
};

export function isAuthorizers(tokens: BusinessTokens): tokens is Authorizers {
  return (
    !!tokens.fb_ad_access_token && !!tokens.fb_page_authorizer_access_token
  );
}

export function isSystemTokens(tokens: BusinessTokens): tokens is SystemUsers {
  return !!tokens.ad_account_token && !!tokens.page_token;
}

// looser than isSystemTokens(): true if the business has been connected via Facebook Builder for at
// least one asset type, even if others are still unconnected or on Authorizers (a real mid-migration
// state — a business isn't guaranteed to have all of ad/page/dataset on system users at once).
export function hasAnySystemUserToken(tokens: BusinessTokens): boolean {
  return (
    !!tokens.ad_account_token ||
    !!tokens.page_token ||
    !!tokens.dataset_token ||
    !!tokens.offline_token ||
    !!tokens.instagram_token
  );
}

type TokenType = 'ad' | 'page' | 'dataset' | 'offline' | 'instagram';

// thrown by getToken() when a business genuinely has no token at all for the requested type — distinct
// from a live Facebook API failure, so callers can tell "nothing to check" apart from "FB rejected us".
export class MissingTokenError extends Error {
  constructor(type: TokenType) {
    super(`No ${type} token available for this business.`);
    this.name = 'MissingTokenError';
  }
}

/**
 * Determines where to pull the token type from given the business tokens.
 *
 * A business is expected to be cleanly all-Authorizers or all-SystemUsers, but a business mid-migration
 * (e.g. one asset just reconnected via Facebook Builder, another not yet) can genuinely have a
 * SystemUser token for one asset type and nothing for another — isAuthorizers()/isSystemTokens() both
 * require every field of their shape to be populated, so neither matches and a strict "pick one shape"
 * lookup would throw. Falls back to reading whichever field (Authorizer or SystemUser) is actually
 * populated for this specific type, so one incompletely-migrated asset doesn't take down checks for
 * every other asset on the same business.
 */
export function getToken(
  tokens: BusinessTokens,
  type: Exclude<TokenType, 'dataset' | 'offline' | 'instagram'>,
): string;
export function getToken(
  tokens: BusinessTokens,
  type: Exclude<TokenType, 'ad' | 'page'>,
): string | null | undefined;
export function getToken(
  tokens: BusinessTokens,
  type: TokenType,
): string | null | undefined {
  if (isAuthorizers(tokens)) {
    return authorizerToken(tokens, type);
  }
  if (isSystemTokens(tokens)) {
    return systemUserToken(tokens, type);
  }

  // neither shape fully matches — fall back per-field to whichever source is actually populated,
  // rather than throwing just because the business as a whole doesn't cleanly fit one shape. Still
  // throws if genuinely nothing is set for this specific type.
  const authorizer = authorizerToken(tokens, type);
  if (authorizer) return authorizer;
  const systemUser = systemUserToken(tokens, type);
  if (systemUser) return systemUser;
  if (type === 'dataset' || type === 'offline' || type === 'instagram')
    return systemUser ?? authorizer;
  throw new MissingTokenError(type);
}

function authorizerToken(
  tokens: BusinessTokens,
  type: TokenType,
): string | null | undefined {
  switch (type) {
    case 'ad':
    case 'dataset':
    case 'offline':
    case 'instagram':
      return tokens.fb_ad_access_token;
    case 'page':
      return tokens.fb_page_authorizer_access_token;
    default:
      throw new Error(`Unknown token type: '${type}'.`);
  }
}

function systemUserToken(
  tokens: BusinessTokens,
  type: TokenType,
): string | null | undefined {
  switch (type) {
    case 'ad':
      return tokens.ad_account_token;
    case 'page':
      return tokens.page_token;
    // a dataset connected to an ad account no longer gets its own system-user grant at all (DRI-214) —
    // access is scoped through the ad account relationship instead, so dataset_token/offline_token are
    // never populated going forward. Read the ad account's own token directly, mirroring what
    // authorizerToken's dataset/offline case already does (it's always read tokens.fb_ad_access_token,
    // never a separate dataset-scoped authorizer token).
    case 'dataset':
    case 'offline':
      return tokens.ad_account_token;
    case 'instagram':
      return tokens.instagram_token;
    default:
      throw new Error(`Unknown token type: '${type}'.`);
  }
}

export type LegacyBusiness = {
  ingest_access_token: string;
  id: number;
  business_name?: string;
  website?: string;
  fb_account_id: string;
  fb_page_id?: string;
  branch_id?: number;
  first_initial_purchase?: number;
  start?: string;
  focus?: number;
  offline_event_set_id?: string | null;
  fb_dataset_id?: string | null;
  attr_windows: AttrWindowsBySourceType;
  lead_webhook_format?: LeadWebhookFormat;
  lead_webhook_email?: 'off' | 'otf';
  lookback_window: number;
  options?: BusinessManagerBusinessOption[];
  currency_type: string;
  timezone: string;
  yvc_spend?: number;
  yvc_party_size?: number;
  yvc_visits?: number;
  override_yvc?: boolean;
  show_roi?: boolean;
  repeat_check_fullname_fallback?: boolean;
  allow_lead_collection?: boolean;
  city?: string;
  state?: string;
  fb_leads_polling_form_ids?: string[];
  country: CountryCode;
  errors?: BusinessError[];
  tokens: BusinessTokens;
};

export interface BusinessManagerBusinessOption {
  id: number;
  branch_id: number;
  business_id: number;
  branch_options_id: number;
  field_value: string;
  option: {
    id: number;
    branch_id: number;
    name: string;
  };
}

export interface BusinessManagerStage {
  id?: number;
  name: string;
  desc: string;
  icon: string;
  support: Support;
  groups: BusinessManagerGroup[];
  estimate: boolean;
  order: number;
  conversion_name: string;
  est_conversion_name: string;
  show_total: boolean;
  is_kpi: boolean;
  is_sub_kpi: boolean;
  show_overview: boolean;
  cost_per_metric: number;
  data_sources_id?: number;
  campaign_goal?: string;
  stage_target?: StageTarget;
}

export interface BusinessManagerGroup {
  id?: number;
  name: string;
  desc: string;
  sources: string[];
  extrapolation_type: ExtrapolationType;
}

export interface BusinessTag {
  id: number;
  label: string;
  color_hex: string;
  type: number;
  created_at?: Date;
  updated_at?: Date;
}
